import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/admin/ui";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { UserPlus, Shield, Trash2 } from "lucide-react";

const PERM_LABELS = {
  view_clients: "View clients",
  add_clients: "Add clients",
  edit_clients: "Edit clients",
  create_service_drafts: "Create service drafts",
  view_payments: "View payment status",
  handle_support: "Handle support",
};

export default function Team() {
  const [members, setMembers] = useState([]);
  const [perms, setPerms] = useState([]);
  const [invite, setInvite] = useState({ email: "", name: "", permissions: [] });

  const load = () => api.get("/team").then((r) => { setMembers(r.data.members); setPerms(r.data.available_permissions); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = (list, setList, p) =>
    setList(list.includes(p) ? list.filter((x) => x !== p) : [...list, p]);

  const sendInvite = async () => {
    if (!invite.email) { toast.error("Email required"); return; }
    try {
      await api.post("/team/invite", invite);
      toast.success("Staff invited");
      setInvite({ email: "", name: "", permissions: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not invite"); }
  };

  const updatePerms = async (m, next) => {
    try { await api.put(`/team/${m.user_id}/permissions`, { permissions: next }); toast.success("Updated"); load(); }
    catch (e) { toast.error("Could not update"); }
  };

  const remove = async (m) => {
    try { await api.delete(`/team/${m.user_id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error("Could not remove"); }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Team" subtitle="Manage administrators and staff permissions" />

      <div className="bg-white border border-[#E2EBF0] rounded-xl p-5 mb-5">
        <h2 className="font-heading font-semibold text-[#0D3B2D] flex items-center gap-2 mb-4">
          <UserPlus className="h-4 w-4 text-[#10B981]" /> Invite Staff
        </h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Email</Label>
            <Input value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })}
              data-testid="invite-email-input" className="bg-white border-[#E2EBF0]" placeholder="staff@company.com" />
          </div>
          <div>
            <Label>Name</Label>
            <Input value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })}
              data-testid="invite-name-input" className="bg-white border-[#E2EBF0]" placeholder="Optional" />
          </div>
        </div>
        <div className="mt-4">
          <Label>Permissions</Label>
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            {perms.map((p) => (
              <label key={p} className="flex items-center gap-2 text-sm text-[#0D3B2D] cursor-pointer">
                <Checkbox checked={invite.permissions.includes(p)}
                  onCheckedChange={() => toggle(invite.permissions, (v) => setInvite({ ...invite, permissions: v }), p)}
                  data-testid={`invite-perm-${p}`} />
                {PERM_LABELS[p]}
              </label>
            ))}
          </div>
        </div>
        <Button onClick={sendInvite} data-testid="send-invite-button"
          className="mt-4 bg-[#0D3B2D] hover:bg-[#071A13] text-white rounded-lg">Send Invite</Button>
      </div>

      <h2 className="font-heading font-semibold text-[#0D3B2D] mb-3">Members</h2>
      <div className="space-y-3" data-testid="team-members-list">
        {members.map((m) => (
          <div key={m.user_id} data-testid={`member-${m.user_id}`} className="bg-white border border-[#E2EBF0] rounded-xl p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                {m.role === "super_admin" && <Shield className="h-4 w-4 text-[#10B981]" />}
                <div>
                  <div className="font-medium text-[#0D3B2D]">{m.name || m.email}</div>
                  <div className="text-xs text-[#4A635A]">{m.email}</div>
                </div>
                <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-[#F4FFF9] border border-[#BCECD8] text-[#10B981] capitalize">
                  {m.role.replace("_", " ")}
                </span>
              </div>
              {m.role === "staff" && (
                <Button onClick={() => remove(m)} variant="ghost" size="sm" data-testid={`remove-${m.user_id}`}
                  className="text-red-500 rounded-lg text-xs"><Trash2 className="h-3.5 w-3.5" /></Button>
              )}
            </div>
            {m.role === "staff" && (
              <div className="grid sm:grid-cols-3 gap-2 mt-3 pt-3 border-t border-[#E2EBF0]">
                {perms.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-xs text-[#0D3B2D] cursor-pointer">
                    <Checkbox checked={(m.permissions || []).includes(p)}
                      onCheckedChange={() => {
                        const cur = m.permissions || [];
                        updatePerms(m, cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);
                      }}
                      data-testid={`member-perm-${m.user_id}-${p}`} />
                    {PERM_LABELS[p]}
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
